import { useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useColors } from '../../../features/products/hooks/useMetaData'
import { metaDataApi } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { Input } from '../../../shared/components'
import Button from '../../../shared/components/Button'

export const ColoursTab = () => {
  const { data, isLoading } = useColors()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: ({ name, hexValue }: { name: string; hexValue: string }) => metaDataApi.createColor(name, hexValue),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['colors'] }),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, hexValue }: { id: string; name: string; hexValue: string }) => { await metaDataApi.updateColor(id, name, hexValue) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['colors'] }); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteColor(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['colors'] }),
  })

  const items = (data?.data ?? []) as unknown as Record<string, unknown>[]

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (item: Record<string, unknown>) => (
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full border border-[#E5E7EB]" style={{ backgroundColor: item.hexValue as string }} />
          <span>{item.name as string}</span>
        </div>
      ),
    },
    { key: 'hexValue', label: 'Hex Value' },
  ]

  const renderAddForm = (onDone: () => void) => (
    <ColourForm
      isLoading={createMutation.isPending}
      onSubmit={async (name, hexValue) => { await createMutation.mutateAsync({ name, hexValue }); onDone() }}
      onCancel={onDone}
    />
  )

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <ColourForm
      defaultName={item.name as string}
      defaultHex={item.hexValue as string}
      isLoading={updateMutation.isPending}
      onSubmit={async (name, hexValue) => updateMutation.mutateAsync({ id: item.id as string, name, hexValue })}
      onCancel={onDone}
    />
  )

  return (
    <MetaTable
      title="Colours"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Colour"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}

const ColourForm = ({ defaultName = '', defaultHex = '#000000', isLoading, onSubmit, onCancel }: {
  defaultName?: string
  defaultHex?: string
  isLoading: boolean
  onSubmit: (name: string, hexValue: string) => Promise<void>
  onCancel: () => void
}) => {
  const [name, setName] = useState(defaultName)
  const [hex, setHex] = useState(defaultHex)

  return (
    <div className="flex gap-6 items-start">
      <div className="flex flex-col gap-3">
        <label className="text-[13px] font-[600] text-foreground">Pick Colour</label>
        <HexColorPicker color={hex} onChange={setHex} style={{ width: '180px', height: '150px' }} />
        <div className="flex items-center gap-2 mt-1">
          <div className="w-6 h-6 rounded-full border border-[#E5E7EB]" style={{ backgroundColor: hex }} />
          <span className="text-[13px] font-mono text-muted-foreground">{hex}</span>
        </div>
      </div>
      <div className="flex flex-col gap-3 flex-1 pt-6">
        <Input label="Colour name" placeholder="e.g. Olive, Burgundy" value={name} onChange={e => setName(e.target.value)} className="h-[38px]" />
        <div className="flex gap-3 mt-2">
          <Button size="sm" onClick={() => onSubmit(name, hex)} disabled={!name.trim() || isLoading} loading={isLoading} className="h-[38px]">
            {defaultName ? 'Update' : 'Save'}
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
        </div>
      </div>
    </div>
  )
}
